import React, {Component} from 'react';
import axios from 'axios';
import {connect} from "react-redux";
import css from './PhotoPack.module.scss';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircle } from '@fortawesome/free-solid-svg-icons';
import moment from 'moment';
import { fetchS3Photos, uploadSinglePhoto } from '../../utils/awsHelper';

/**
 * @class
 *
 * PhotoPack page.
 */
const ServiceCallPhotoPack = class extends Component {
    // ---------- class fields: ----------
    source = axios.CancelToken.source();
    opportunityId = this.props.opportunityId;
    sessId = this.props.sessId;
    type = this.props.type;
    username = this.props.user.name;
    SF_DATE_TIME_FORMAT = "YYYY-MM-DDTHH:mm:ssZ";

    postRequestOpp = {
        "limit": 1,
        "skip": 0,
        "fields": [
            "Id",
            "Name",
            "Company__c",
            "Substantial__c",
            "Substantial__r.Name",
            "Ground_Mount__c",
            "Days_to_Clawback__c",
            "Site_Visits__c",
            "Location__r.Name"
        ],
        "where": {
            "Id": this.opportunityId
        }
    };

    postRequestSession = {
        "limit": 1,
        "skip": 0,
        "fields": [
            "Id",
            "Name",
            "Field_Job__c",
            "Field_Job__r.Clocked_End__c",
            "Field_Job__r.Has_the_problem_been_fixed__c",
            "Case__c",
            "Case__r.Prioritize_for_Substantial__c",
            "Case__r.Prioritize_for_M3__c",
            "Assigned_Crew__r.Work_Phone_Number__c"
        ],
        "where": {
            "Id": this.sessId
        }
    };

    postRequestWorkOrder = {
        "limit": 1,
        "skip": 0,
        "fields": [
            "Id",
            "Name",
            "Assigned_Field_Team__c", 
            "Assigned_Field_Team__r.Tech_1__c",
            "Assigned_Field_Team__r.Tech_1__r.Work_Phone_Number__c",
            "Job_Overview__r.Stage__c",
            "Review_Submitted__c",
            "Number_of_Days__c"
        ],
        "where": {
            "Id": this.sessId
        }
    };

    state = {
        preview: false,
        previewPic: {},
        previewPics: [],
        filesToUpload: [],
        uploading: false,
        fileUploadCounter: 0,
        errorMessage: '',
        opp: {},
        session: {},
        slackResponse: '',
        sendingToSlack: false,
        attachedFiles:[],
        fieldJobDone: false,
        notPriortized: false,
        tier3rtr: false,
        isPast6PM: false,
        isWorkOrder: false,

        ServiceCall: {},
        ServiceCallError: false,
    }

    // ---------- life cycle methods: ----------
    async componentDidMount() {
        window.scrollTo(0, 0);
        await this.getDocs();

        const now = new Date();
        const pstTime = moment(now, "America/Los_Angeles");
        if (pstTime.hour() >= 18) {
            this.setState({ isPast6PM: true });
        }

        await this.getOpp();

        const isWorkOrder = this.sessId.startsWith('a0j');
        this.setState({ isWorkOrder });
        if (isWorkOrder) {
            await this.getWorkOrder();
        } else {
            await this.getSession();
        }
    }

    componentWillUnmount() {
        this.source.cancel();
    }

    getDocs = async() =>{
        fetchS3Photos({
            recordId: this.opportunityId,
            type: '/' + this.type,
            process: 'getDocs',
            existingState: this.state,
            source: this.source
        })
        .then(returnedFiles => {
             if(returnedFiles.length !== 0){
                this.setState({attachedFiles: returnedFiles})
            }
        })
        .catch(err => {
            console.error('AWS S3 fetch error:', err);
            this.setState({ errorMessage: err.message });
        });
    }

    getOpp = async() => {
        axios.post(
            '/sf/opportunity/findNo',
            this.postRequestOpp,
            {
                cancelToken: this.source.token
            })
            .then((res) => {
                const data = res.data.records[0];
                this.setState({opp: data});

                if((data.Days_to_Clawback__c !== null && data.Days_to_Clawback__c < 30) || data.Site_Visits__c >= 6) {
                    this.setState({tier3rtr: true});
                }
            })
            .catch(err => console.log('POST Error Opp:\n', err));
    };

    getSession = async() => {
        axios.post(
            '/sf/schedulable/findNo',
            this.postRequestSession,
            {
                cancelToken: this.source.token
            })
            .then((res) => {
                const data = res.data.records[0];

                if(data.Case__c !== null && data.Case__r.Prioritize_for_Substantial__c === false && data.Case__r.Prioritize_for_M3__c === false){
                    this.setState({notPriortized: true});
                }

                this.setState({session: data});
            })
            .catch(err => console.log('POST Error Session:\n', err));
    };

    getWorkOrder = async() => {
        axios.post(
            '/sf/WorkOrder/findNo',
            this.postRequestWorkOrder,
            {
                cancelToken: this.source.token
            })
            .then((res) => {
                const data = res.data.records[0];

                if(data.Job_Overview__r?.Stage__c === 'Post-Install'){
                    this.setState({notPriortized: true});
                }

                this.setState({session: data});
            })
            .catch(err => console.log('POST Error Session:\n', err));
    }

    sanitizeFileName = (input) => {
        if (!input) return "file";

        const dot = input.lastIndexOf(".");
        const baseRaw = dot > 0 ? input.slice(0, dot) : input;
        const ext = dot > 0 ? input.slice(dot).toLowerCase() : "";

        let base = baseRaw.normalize("NFKD").replace(/\p{Diacritic}/gu, "");
        base = base.replace(/\s+/g, "-");
        base = base.replace(/[^a-zA-Z0-9._-]/g, "");
        base = base.replace(/[._-]{2,}/g, "-");
        base = base.replace(/^[-._]+|[-._]+$/g, "");
        base = base.toLowerCase();
        if (!base) base = "file";
        const MAX_BASE_LEN = 200;
        if (base.length > MAX_BASE_LEN) base = base.slice(0, MAX_BASE_LEN);

        return base + ext;
    };

    capturePhoto = async(files, stateNameError) => {
        if (!files || files.length === 0) return;
        for (const file of files) {
            const stateName = this.sanitizeFileName(file.name);
            try {
                await uploadSinglePhoto({
                    file,
                    stateName,
                    stateNameError,
                    username: this.username,
                    opportunityId: this.opportunityId,
                    type: this.type,
                    setState: this.setState.bind(this),
                    source: this.source,
                });
            } catch (err) {
                console.log('Upload failed for', stateName, err);
            }
        }

        await this.getDocs();
    };

    previewImage = (e) => {
        let img = e;
        this.setState({previewPic: img, preview:true});
    };

    sendToSlack = () => {
        this.setState({sendingToSlack: true});

        let message = 'Service Call Photos Review for: <https://v3electric.my.site.com/frontendOps/s/detail/' +  this.state.opp.Id + '|' +  this.state.opp.Name +'>\n' +
            'Field Tech: ' + this.props.user.name + '\n' +
            'Photo Pack Page: <https://v3electric.my.site.com/frontendOps/s/sfdcpage/%2Fapex%2FPhotoPackOppS3%3F%26id%3D' + this.state.opp.Id + '|Photo Pack>\n' +
            'Work Order: <https://v3electric.my.site.com/frontendOps/s/detail/' + this.state.session.Id + '|' +  this.state.session.Name +'>\n' +
            'Substantial: <https://v3electric.my.site.com/frontendOps/s/detail/' + this.state.opp.Substantial__c + '|' +  this.state.opp.Substantial__r.Name +'>\n' +
            'Financier: ' + this.state.opp.Company__c + '\n' +
            'Location: ' + this.state.opp.Location__r.Name + '\n' +
            'Number of Days: ' + this.state.session.Number_of_Days__c;

        // test-api channel id C06JN84MGFM
        // tier 1 rtr id: C07UMTF64TE
        // tier 2 rtr id: C07UGHTQ7M3
        // tier 3 rtr id: C07V87JGTFS

        let channelId = '';

        if((this.state.opp.Days_to_Clawback__c > 30 || this.state.opp.Days_to_Clawback__c === null)&& this.state.opp.Site_Visits__c < 6) {
            channelId = 'C07UGHTQ7M3'; // tier 2
        } else if((this.state.opp.Days_to_Clawback__c !== null && this.state.opp.Days_to_Clawback__c < 30) || this.state.opp.Site_Visits__c >= 6) {
            channelId = 'C07V87JGTFS'; // tier 3
            if(this.state.isWorkOrder){
                message = message + '\nTechs Work Phone Number: ' + this.state.session.Assigned_Field_Team__r.Tech_1__r.Work_Phone_Number__c;
            } else {
                message = message + '\nTechs Work Phone Number: ' + this.state.session.Assigned_Crew__r.Work_Phone_Number__c;
            }
        } else {
            channelId = 'C07UMTF64TE'; // default to tier 1
        }

        let payload = {
            "channel": channelId,
            "text": message
        };

        axios.post(
            '/slackMessage', 
            payload,
            {
                cancelToken: this.source.token
            })
            .then((res) => {
                this.setState({slackResponse: 'Notification sent to Slack successfully', sendingToSlack: false});

                let subPayload = {
                    Id: this.state.opp.Substantial__c,
                    Service_Review_SubmittedDT__c: moment()
                };

                axios.put(
                    '/sf/substantial/update',
                    subPayload,
                    {
                        cancelToken: this.source.token
                    }).then((res) => {
                        
                    })
                    .catch(err => console.log('PUT Error updating substantial:\n', err))

                // Review Submitted on WO is used for reporting on when field team initially requested RTR.
                if(this.state.isWorkOrder && !this.state.session.Review_Submitted__c){
                    let woPayload = {
                        Id: this.sessId,
                        Review_Submitted__c: moment().format('YYYY-MM-DDTHH:mm:ssZ'),
                    };

                    axios.put(
                        '/sf/WorkOrder/update', 
                        woPayload, 
                        {
                            cancelToken: this.source.token
                        })
                        .catch(err => console.log('PUT Error Updating WO on RTR Request:\n', err));
                }

                setTimeout(() => {
                    this.setState({slackResponse: ''});
                }, 3000);
            })
            .catch(err => {
                console.log('POST Error Sending Slack Message:\n', err);
                this.setState({slackResponse: 'Failed to send to Slack: ' + err.message, sendingToSlack: false});
            });
    };

    render() {
        return (
            <div className={css.PhotoPack}>
                <h1 className='pb-3'>Service Call Photo Pack</h1>
                <h2 className='text-white pb-3'>Please Ensure HEIC is turned off as the format isn't readily sharable</h2>
                <h2 className={this.state.errorMessage !== '' ? 'text-error' : 'd-none'}>Error: {this.state.errorMessage}</h2>
                
                {
                    this.state.preview ? 
                    <div>
                        <div>
                            <img className={css.PhotoPack__PreviewImg} src={this.state.previewPic} alt="Preview of upload" />
                        </div>
                        <div className='pt-3'>
                            <button className='btn btn-primary' onClick={() => this.setState({preview: false, previewPic: {}})}>Close Preview</button>
                        </div>
                    </div>

                    :

                    <div>
                        {/* Slack div to send messages */}
                        <div className='py-3'>
                            <button className='btn btn-primary' disabled={this.state.notPriortized ? this.state.notPriortized : this.state.sendingToSlack} onClick={() => this.sendToSlack()}>Notify Office for Review</button>
                        </div>
                        <div className={this.state.slackResponse !== '' ? 'py-3' : 'd-none'}>
                            <p className='text-white'>{this.state.slackResponse}</p>
                        </div>
                        <div className={this.state.sendingToSlack ? 'py-3' : 'd-none'}>
                            <div className={css.PhotoPack__Loader}></div>
                        </div>

                        {
                            this.state.notPriortized ? 
                            <h2 className='text-white pb-3'>No RTR needed for this Service Call. Please take needed pictures then proceed to your next appointment.</h2>
                            :
                            <></>
                        }

                        {
                            this.state.tier3rtr ? 
                                this.state.isPast6PM ? 
                                <h2 className='text-white pb-3'>This is a Tier 3 project. Reviewers are unavailable at this time. Please be prepared to have a phone review tomorrow morning.</h2>
                                :
                                <h2 className='text-white pb-3'>This is a Tier 3 project. Please sit tight until you receive a call from the RTR team to conduct the review.</h2>
                                :
                                <></>
                        }

                        <table>
                            <tbody>

                                {/* All Photos*/}
                                <tr>
                                    <td className="w-50">
                                        {
                                            this.state.ServiceCallError ?
                                            <div className='text-error'><FontAwesomeIcon icon={faCircle} /> Error Uploading Photos: <br></br>{this.state.errorMessage}</div>
                                            :
                                            <div className='text-warningC'><FontAwesomeIcon icon={faCircle} /> All Photos</div>
                                        }
                                    </td>
                                    <td className="w-50">
                                        <label htmlFor="ServiceCallUpload" className="btn btn-primary"><div className={this.state.uploading ? css.PhotoPack__Uploading : 'd-none'}></div>Upload from Phone</label>
                                        <input className={css.PhotoPack__PicInput} id="ServiceCallUpload" type="file" accept="image/*" disabled={this.state.uploading} multiple onChange={(event) => this.capturePhoto(event.target.files, 'ServiceCallError')}/>
                                    </td>
                                </tr>
                                {this.state.attachedFiles &&
                                    Object.entries(this.state.attachedFiles).map(([name, url]) => (
                                    <tr key={name}>
                                        <td className="w-50">
                                            <button className="btn btn-outline text-green" onClick={() => this.previewImage(url)}>
                                                <FontAwesomeIcon icon={faCircle} /> {name}
                                            </button>
                                        </td>
                                        <td className="w-50">
                                            <button className="btn btn-primary" onClick={() => this.previewImage(url)}>
                                                View Pic
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>

                        {/* Slack div to send messages */}
                        <div className='py-3'>
                            <button className='btn btn-primary' disabled={this.state.notPriortized ? this.state.notPriortized : this.state.sendingToSlack} onClick={() => this.sendToSlack()}>Notify Office for Review</button>
                        </div>
                        <div className={this.state.slackResponse !== '' ? 'py-3' : 'd-none'}>
                            <p className='text-white'>{this.state.slackResponse}</p>
                        </div>
                        <div className={this.state.sendingToSlack ? 'py-3' : 'd-none'}>
                            <div className={css.PhotoPack__Loader}></div>
                        </div>
                    </div>
                }

                <h2 className={this.state.errorMessage !== '' ? 'text-error' : 'd-none'}>Error: {this.state.errorMessage}</h2>

            </div>
        )
    }
};

// ---------- Redux mapping: ----------
const mapStateToProps = state => {
    return {
        user: state.user
    };
};

export default connect(mapStateToProps)(ServiceCallPhotoPack);